// Parses and checks formulas like `if({Stage} = "Offer", {Salary} * 0.1, 0)`.
// Running one is the server's job: evaluate_formula answers in the units the
// save writes, which the browser cannot know.

type TokenKind =
  | 'number'
  | 'string'
  | 'field'
  | 'name'
  | 'operator'
  | 'open'
  | 'close'
  | 'comma';

export interface Token {
  kind: TokenKind;
  text: string;
  start: number;
}

export interface FormulaIssue {
  message: string;
  // The offending piece of the formula, e.g. `{Salry}` or `**`.
  text: string;
  start: number;
}

export type FormulaValue = number | string | boolean | null;

type Node =
  | { type: 'literal'; value: FormulaValue }
  | { type: 'field'; token: string }
  | { type: 'unary'; operator: '-' | 'NOT'; operand: Node }
  | { type: 'binary'; operator: string; left: Node; right: Node }
  | { type: 'call'; name: string; args: Node[] };

class FormulaSyntaxError extends Error {
  text: string;
  start: number;

  constructor(message: string, text: string, start: number) {
    super(message);
    this.text = text;
    this.start = start;
  }
}

// `%` and `<>` are published by the server; refusing them here failed formulas
// it accepts. `!=` stays too — both spellings parse.
const FORMULA_OPERATORS = [
  '+',
  '-',
  '*',
  '/',
  '%',
  '=',
  '!=',
  '<>',
  '>',
  '<',
  '>=',
  '<=',
];
const LOGICAL = ['AND', 'OR'];
// Words the language treats as operators, never as function names.
const LOGICAL_WORDS = [...LOGICAL, 'NOT'];
const COMPARISON = ['=', '!=', '<>', '>', '<', '>=', '<='];
const OPERATOR_CHARS = /[+\-*/=!<>&|^%]/;

// Splits a formula into tokens, reporting operators and characters the language doesn't have.
const tokenize = (formula: string) => {
  const tokens: Token[] = [];
  const issues: FormulaIssue[] = [];
  let i = 0;
  const push = (kind: TokenKind, text: string, start: number) =>
    tokens.push({ kind, text, start });

  while (i < formula.length) {
    const char = formula[i];
    const start = i;
    if (/\s/.test(char)) {
      i += 1;
    } else if (char === '{') {
      const end = formula.indexOf('}', i);
      if (end === -1) {
        issues.push({
          message: 'This field is missing a closing }',
          text: formula.slice(i),
          start,
        });
        break;
      }
      push('field', formula.slice(i + 1, end).trim(), start);
      i = end + 1;
    } else if (char === '"') {
      const end = formula.indexOf('"', i + 1);
      if (end === -1) {
        issues.push({
          message: 'This text is missing a closing "',
          text: formula.slice(i),
          start,
        });
        break;
      }
      push('string', formula.slice(i + 1, end), start);
      i = end + 1;
    } else if (/[0-9.]/.test(char)) {
      const match = /^[0-9]*\.?[0-9]+/.exec(formula.slice(i));
      if (match) {
        push('number', match[0], start);
        i += match[0].length;
      } else {
        issues.push({
          message: 'This character is not allowed',
          text: char,
          start,
        });
        i += 1;
      }
    } else if (/[A-Za-z_]/.test(char)) {
      const text = /^[A-Za-z_][A-Za-z0-9_]*/.exec(formula.slice(i))![0];
      push('name', text, start);
      i += text.length;
    } else if (char === '(' || char === ')' || char === ',') {
      push(
        char === '(' ? 'open' : char === ')' ? 'close' : 'comma',
        char,
        start
      );
      i += 1;
    } else if (OPERATOR_CHARS.test(char)) {
      let text = char;
      while (i + text.length < formula.length) {
        const next = formula[i + text.length];
        if (!OPERATOR_CHARS.test(next) || next === '-') break;
        text += next;
      }
      if (FORMULA_OPERATORS.includes(text)) push('operator', text, start);
      else
        issues.push({ message: 'This operator does not exist', text, start });
      i += text.length;
    } else {
      issues.push({
        message: 'This character is not allowed',
        text: char,
        start,
      });
      i += 1;
    }
  }
  return { tokens, issues };
};

// Tokens as the editor's highlighter sees them, gaps and all.
export const formulaTokens = (formula: string) => tokenize(formula).tokens;

// Recursive descent: or → and → comparison → sum → product → unary → primary.
const parse = (tokens: Token[]): Node | null => {
  let position = 0;
  const peek = () => tokens[position];
  const fail = (message: string, token?: Token): never => {
    throw new FormulaSyntaxError(
      message,
      token?.text ?? '',
      token?.start ?? -1
    );
  };
  const isLogical = (token: Token | undefined, word: string) =>
    token?.kind === 'name' && token.text.toUpperCase() === word;

  const binary = (
    next: () => Node,
    matches: (token: Token | undefined) => boolean,
    operatorOf: (token: Token) => string
  ) => {
    let left = next();
    while (matches(peek())) {
      const operator = operatorOf(tokens[position]);
      position += 1;
      left = { type: 'binary', operator, left, right: next() };
    }
    return left;
  };

  const primary = (): Node => {
    const token = peek();
    if (!token) return fail('This formula is incomplete');
    position += 1;
    if (token.kind === 'number')
      return { type: 'literal', value: Number(token.text) };
    if (token.kind === 'string') return { type: 'literal', value: token.text };
    if (token.kind === 'field') return { type: 'field', token: token.text };
    if (token.kind === 'open') {
      const inner = orExpression();
      if (peek()?.kind !== 'close') fail('This bracket is never closed', token);
      position += 1;
      return inner;
    }
    if (token.kind === 'name' && peek()?.kind === 'open') {
      position += 1;
      const args: Node[] = [];
      while (peek() && peek().kind !== 'close') {
        args.push(orExpression());
        if (peek()?.kind === 'comma') position += 1;
        else if (peek()?.kind !== 'close') fail('Expected , or )', peek());
      }
      if (!peek()) fail('This bracket is never closed', token);
      position += 1;
      return { type: 'call', name: token.text, args };
    }
    return fail(`Unexpected ${token.text}`, token);
  };

  const unary = (): Node => {
    if (peek()?.kind === 'operator' && peek().text === '-') {
      position += 1;
      return { type: 'unary', operator: '-', operand: unary() };
    }
    return primary();
  };
  const isOperator = (list: string[]) => (token?: Token) =>
    token?.kind === 'operator' && list.includes(token.text);
  const product = () =>
    binary(unary, isOperator(['*', '/', '%']), (t) => t.text);
  const sum = () => binary(product, isOperator(['+', '-']), (t) => t.text);
  const comparison = () => binary(sum, isOperator(COMPARISON), (t) => t.text);
  // `not` is a function here, never a prefix: the server's catalog publishes
  // it with the other functions and refuses `not {a}`, so accepting the prefix
  // form let a formula pass the editor and fail on save.
  const notExpression = (): Node => comparison();
  const andExpression = () =>
    binary(
      notExpression,
      (t) => isLogical(t, 'AND'),
      () => 'AND'
    );
  const orExpression = (): Node =>
    binary(
      andExpression,
      (t) => isLogical(t, 'OR'),
      () => 'OR'
    );

  if (tokens.length === 0) return null;
  const tree = orExpression();
  // A bare word after a whole expression is a mistyped `and`, `or` or `not`.
  if (position < tokens.length)
    fail(
      peek().kind === 'name'
        ? 'This operator does not exist'
        : `Unexpected ${peek().text}`,
      peek()
    );
  return tree;
};

const sameToken = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

// Every problem in a formula, in the order it appears.
export const validateFormula = (
  formula: string,
  knownFields: string[],
  knownFunctions: string[],
  // The token of the field being written. It is absent from `knownFields` —
  // the server leaves it out of what a formula may reference — so without
  // this a field naming itself reads as one that does not exist.
  ownToken?: string,
  // Fields the catalog has but a formula cannot compute over, such as a select
  // or a file. They exist, so saying they do not sends the author looking for a
  // field that is right in front of them.
  uncomputableFields: string[] = []
): FormulaIssue[] => {
  const { tokens, issues } = tokenize(formula);
  tokens.forEach((token) => {
    if (token.kind !== 'field') return;
    if (ownToken && sameToken(ownToken, token.text)) {
      issues.push({
        message: 'A field cannot calculate itself',
        text: `{${token.text}}`,
        start: token.start,
      });
      return;
    }
    if (knownFields.some((field) => sameToken(field, token.text))) return;
    issues.push({
      message: uncomputableFields.some((field) => sameToken(field, token.text))
        ? 'This field cannot be used in a formula'
        : 'This field does not exist',
      text: `{${token.text}}`,
      start: token.start,
    });
  });
  tokens.forEach((token, index) => {
    const isCall = tokens[index + 1]?.kind === 'open';
    // `and (`, `or (`, `not (` look like calls but are operators — the bracket
    // belongs to what they join, not to them.
    const isLogicalWord = LOGICAL_WORDS.some((word) =>
      sameToken(word, token.text)
    );
    if (
      token.kind === 'name' &&
      isCall &&
      !isLogicalWord &&
      !knownFunctions.some((name) => sameToken(name, token.text))
    )
      issues.push({
        message: 'This function does not exist',
        text: token.text,
        start: token.start,
      });
  });
  if (issues.length === 0) {
    try {
      parse(tokens);
    } catch (error) {
      if (!(error instanceof FormulaSyntaxError)) throw error;
      issues.push({
        message: error.message,
        text: error.text,
        start: error.start,
      });
    }
  }
  return issues.sort((a, b) => a.start - b.start);
};

// Field tokens a formula reads, once each, in the order they first appear.
export const referencedFields = (formula: string) => {
  const seen: string[] = [];
  tokenize(formula).tokens.forEach((token) => {
    if (token.kind === 'field' && !seen.some((f) => sameToken(f, token.text)))
      seen.push(token.text);
  });
  return seen;
};
